import React, { useState, useEffect, useRef, useContext } from 'react';
import CountryDropdown from '../../components/elements/CountryDropdown';
import { Input, Space, Typography, Table, Button, Drawer, Popover, message, Modal } from 'antd';
import { AuthContext } from '../../services/auth.service';
import { SearchOutlined } from '@ant-design/icons';
import './styles.scss';
import localStorageService from '../../services/localStorage.service';
const { Text } = Typography;
const modalHeaderName = ("settings");


// Side menu content for Profile Details
export const ProfileDetails = ({ context }) => {
    const {
        firstName,
        lastName,
        email,
        companyId,
        setModalSaveStatus,
        setConfigParams,
        navigate,
    } = useContext(context);

    const initialFirstName = useRef(firstName);
    const initialLastName = useRef(lastName);
    const initialEmail = useRef(email);

    const [curFirstName, setCurFirstName] = useState(firstName);
    const [curLastName, setCurLastName] = useState(lastName);
    const [curEmail, setCurEmail] = useState(email);

    const [firstNameStatus, setFirstNameStatus] = useState(firstName?.length > 0);
    const [lastNameStatus, setLastNameStatus] = useState(lastName?.length > 0);
    const [emailStatus, setEmailStatus] = useState(email?.length > 0);

    const [saveStatus, setSaveStatus] = useState(false);
    const [configParamsToSave, setConfigParamsToSave] = useState({});

    const checkSaveStatus = () => {
        setSaveStatus(
            curFirstName?.length > 0 &&
            curLastName?.length > 0 &&
            curEmail?.length > 0 &&
            (curFirstName !== initialFirstName.current || curLastName !== initialLastName.current || curEmail !== initialEmail.current)
        );
    };

    useEffect(() => {
        setCurFirstName(firstName);
        setCurLastName(lastName);
        setCurEmail(email);
    }, [firstName, lastName, email]);

    useEffect(() => {
        setFirstNameStatus(curFirstName?.length > 0);
        setLastNameStatus(curLastName?.length > 0);
        setEmailStatus(curEmail?.length > 0);

        checkSaveStatus();

        const newObject = { ...configParamsToSave };
        const userProfile = {
            first_name: curFirstName,
            last_name: curLastName,
            email: curEmail,
        };
        newObject["user_profile"] = userProfile;

        setConfigParamsToSave(newObject);
    }, [curFirstName, curLastName, curEmail]);

    useEffect(() => setSaveStatus(false), []);

    useEffect(() => setModalSaveStatus(saveStatus), [saveStatus]);

    useEffect(() => setConfigParams(configParamsToSave), [configParamsToSave]);


    return (
        <Space key={companyId} direction="vertical" size="large">
            <Space direction="vertical" size="small">
                <Space direction="vertical" size="small">
                    <h2 className='header-letter-spacing'>User profile</h2>
                    <p>Fill in your personal details, which will be linked to your Code Cube account.</p>
                </Space>
                <Space direction="vertical" size="large">
                    <Space direction="horizontal" size={50}>
                        <Space direction="vertical" size="small" style={{ flex: 1 }}>
                            <Text strong>First name *</Text>
                            <Input
                                value={curFirstName}
                                onChange={(e) => setCurFirstName(e.target.value)}
                                status={firstNameStatus ? "" : "error"}
                                style={{ width: '250px' }}
                            />
                        </Space>
                        <Space direction="vertical" size="small" style={{ flex: 1 }}>
                            <Text strong>Last name *</Text>
                            <Input
                                value={curLastName}
                                onChange={(e) => setCurLastName(e.target.value)}
                                status={lastNameStatus ? "" : "error"}
                                style={{ width: '250px' }}
                            />
                        </Space>
                    </Space>
                    <Space direction="vertical" size="small">
                        <Text strong>Email *</Text>
                        <Text>Add your personal email address, this email will also be used as your username in the portal.</Text>
                        <Space.Compact size="middle" style={{ width: '100%' }}>
                            <Input
                                placeholder="hello@code-cube.io"
                                value={curEmail}
                                type="email"
                                onChange={(e) => setCurEmail(e.target.value)}
                                style={{ width: '250px' }}
                            />
                        </Space.Compact>
                    </Space>
                    <Space direction="vertical" size="small">
                        <Text strong>Password</Text>
                        <Text>Change your password.</Text>
                        <Button style={{ fontWeight: '500', borderRadius: '70px' }} type="primary" onClick={() => navigate('/password-reset')}>
                            Reset Password
                        </Button>
                    </Space>
                </Space>
            </Space>
        </Space>
    );
};

// Side menu content for Company Details
export const CompanyDetails = ({ context }) => {
    const {
        companyName,
        chamberOfCommerceNumber,
        vatNumber,
        moneybirdCid,
        billingStreet1,
        billingStreet2,
        billingPostcode,
        billingProvince,
        billingCity,
        billingCountry,
        billingFullName,
        billingEmail,
        invoiceRefNumber,
        companyId,
        setModalSaveStatus,
        setConfigParams,
    } = useContext(context);

    const initialCompanyName = useRef(companyName);
    const initialChamberOfCommerceNumber = useRef(chamberOfCommerceNumber);
    const initialVatNumber = useRef(vatNumber);
    const initialMoneybirdCid = useRef(moneybirdCid);
    const initialBillingStreet1 = useRef(billingStreet1);
    const initialBillingStreet2 = useRef(billingStreet2);
    const initialBillingPostcode = useRef(billingPostcode);
    const initialBillingProvince = useRef(billingProvince);
    const initialBillingCity = useRef(billingCity);
    const initialBillingCountry = useRef(billingCountry);
    const initialBillingFullName = useRef(billingFullName);
    const initialBillingEmail = useRef(billingEmail);
    const initialInvoiceRefNumber = useRef(invoiceRefNumber);

    const [curCompanyName, setCurCompanyName] = useState(companyName);
    const [curChamberOfCommerceNumber, setCurChamberOfCommerceNumber] = useState(chamberOfCommerceNumber);
    const [curVatNumber, setCurVatNumber] = useState(vatNumber);
    const [curBillingStreet1, setCurBillingStreet1] = useState(billingStreet1);
    const [curBillingStreet2, setCurBillingStreet2] = useState(billingStreet2);
    const [curBillingPostcode, setCurBillingPostcode] = useState(billingPostcode);
    const [curBillingProvince, setCurBillingProvince] = useState(billingProvince);
    const [curBillingCity, setCurBillingCity] = useState(billingCity);
    const [curBillingCountry, setCurBillingCountry] = useState(billingCountry);
    const [curBillingFullName, setCurBillingFullName] = useState(billingFullName);
    const [curBillingEmail, setCurBillingEmail] = useState(billingEmail);
    const [curInvoiceRefNumber, setCurInvoiceRefNumber] = useState(invoiceRefNumber);

    const [companyNameStatus, setCompanyNameStatus] = useState(companyName?.length > 0);
    const [chamberOfCommerceStatus, setChamberOfCommerceStatus] = useState(chamberOfCommerceNumber?.length > 0);
    const [billingStreet1Status, setBillingStreet1Status] = useState(billingStreet1?.length > 0);
    const [billingCountryStatus, setBillingCountryStatus] = useState(billingCountry?.length > 0);
    const [fullNameStatus, setFullNameStatus] = useState(billingFullName?.length > 0);
    const [emailStatus, setEmailStatus] = useState(billingEmail?.length > 0);

    const [saveStatus, setSaveStatus] = useState(false);
    const [configParamsToSave, setConfigParamsToSave] = useState({});

    const checkSaveStatus = () => {
        const requiredFieldsFilled = 
            curCompanyName?.length > 0 && 
            curChamberOfCommerceNumber?.length > 0 && 
            curBillingStreet1?.length > 0 && 
            curBillingCountry?.length > 0 &&
            curBillingFullName?.length > 0 &&
            curBillingEmail?.length > 0;

        const anyFieldChanged = 
            curCompanyName !== initialCompanyName.current ||
            curChamberOfCommerceNumber !== initialChamberOfCommerceNumber.current ||
            curVatNumber !== initialVatNumber.current ||
            curBillingStreet1 !== initialBillingStreet1.current ||
            curBillingStreet2 !== initialBillingStreet2.current ||
            curBillingPostcode !== initialBillingPostcode.current ||
            curBillingProvince !== initialBillingProvince.current ||
            curBillingCity !== initialBillingCity.current ||
            curBillingCountry !== initialBillingCountry.current ||
            curBillingFullName !== initialBillingFullName.current ||
            curBillingEmail !== initialBillingEmail.current ||
            curInvoiceRefNumber !== initialInvoiceRefNumber.current;

        setSaveStatus(anyFieldChanged ? (requiredFieldsFilled ? true : false) : false);
    };

    useEffect(() => {
        setCurCompanyName(companyName);
        setCurChamberOfCommerceNumber(chamberOfCommerceNumber);
        setCurVatNumber(vatNumber);
        setCurBillingStreet1(billingStreet1);
        setCurBillingStreet2(billingStreet2);
        setCurBillingPostcode(billingPostcode);
        setCurBillingProvince(billingProvince);
        setCurBillingCity(billingCity);
        setCurBillingCountry(billingCountry);
        setCurBillingFullName(billingFullName);
        setCurBillingEmail(billingEmail);
        setCurInvoiceRefNumber(invoiceRefNumber);
    }, [companyName, chamberOfCommerceNumber, vatNumber, moneybirdCid, billingStreet1, billingStreet2, 
        billingPostcode, billingProvince, billingCity, billingCountry, billingFullName, billingEmail, invoiceRefNumber]);

    useEffect(() => {
        setCompanyNameStatus(curCompanyName?.length > 0);
        setChamberOfCommerceStatus(curChamberOfCommerceNumber?.length > 0);
        setBillingStreet1Status(curBillingStreet1?.length > 0);
        setBillingCountryStatus(curBillingCountry?.length > 0);
        setFullNameStatus(curBillingFullName?.length > 0);
        setEmailStatus(curBillingEmail?.length > 0);

        checkSaveStatus();

        const newObject = { ...configParamsToSave };
        const companyProfile = {
            display_company_name: curCompanyName,
            company_cocn_number: curChamberOfCommerceNumber,
            company_vat_number: curVatNumber,
            billing_street_address_1: curBillingStreet1,
            billing_street_address_2: curBillingStreet2,
            billing_postal_code: curBillingPostcode,
            billing_province: curBillingProvince,
            billing_city: curBillingCity,
            billing_country: curBillingCountry,
        };
        const billingProfile = {
            billing_fullname: curBillingFullName,
            billing_email: curBillingEmail,
            company_invoice_ref_number: curInvoiceRefNumber,
        };
        newObject["company_profile"] = companyProfile;
        newObject["billing_profile"] = billingProfile;

        setConfigParamsToSave(newObject);
    }, [
        curCompanyName,
        curChamberOfCommerceNumber,
        curVatNumber,
        curBillingStreet1,
        curBillingStreet2,
        curBillingPostcode,
        curBillingProvince,
        curBillingCity,
        curBillingCountry,
        curBillingFullName,
        curBillingEmail,
        curInvoiceRefNumber
    ]);

    useEffect(() => setSaveStatus(false), []);

    useEffect(() => setModalSaveStatus(saveStatus), [saveStatus]);

    useEffect(() => setConfigParams(configParamsToSave), [configParamsToSave]);


    return (
        <Space key={companyId} direction="vertical" size={50}>
            {/* Company Profile Section */}
            <Space direction="vertical" size="small">
                <h2 className='header-letter-spacing'>Company profile</h2>
                <p>Fill in the company details in the fields below.</p>

                <Space direction="vertical" size="middle">
                    {/* Company Details Row */}
                    <Space direction="horizontal" size={50}>
                        <Space direction="vertical" size="small" style={{ flex: 1 }}>
                            <Text strong>Company name *</Text>
                            <Input
                                value={curCompanyName}
                                onChange={(e) => setCurCompanyName(e.target.value)}
                                status={companyNameStatus ? "" : "error"}
                                style={{ width: '250px' }}
                            />
                        </Space>
                        <Space direction="vertical" size="small" style={{ flex: 1 }}>
                            <Text strong>Chamber of commerce *</Text>
                            <Input
                                value={curChamberOfCommerceNumber}
                                onChange={(e) => setCurChamberOfCommerceNumber(e.target.value)}
                                status={chamberOfCommerceStatus ? "" : "error"}
                                style={{ width: '250px' }}
                            />
                        </Space>
                    </Space>

                    <Space direction="horizontal" size={50}>
                        <Space direction="vertical" size="small" style={{ flex: 1 }}>
                            <Text strong>VAT number</Text>
                            <Input
                                value={curVatNumber}
                                onChange={(e) => setCurVatNumber(e.target.value)}
                                style={{ width: '250px' }}
                            />
                        </Space>
                    </Space>

                </Space>
            </Space>

            <Space direction="vertical" size="small">
                    <h2 className='header-letter-spacing'>Address details</h2>
                    <p>Fill in the address details in the fields below.</p>

                    {/* Address Row 1 */}
                    <Space direction="horizontal" size={50}>
                        <Space direction="vertical" size="small" style={{ flex: 1 }}>
                            <Text strong>Street address *</Text>
                            <Input
                                value={curBillingStreet1}
                                onChange={(e) => setCurBillingStreet1(e.target.value)}
                                status={billingStreet1Status ? "" : "error"}
                                style={{ width: '250px' }}
                            />
                        </Space>
                        <Space direction="vertical" size="small" style={{ flex: 1 }}>
                            <Text strong>Street address 2</Text>
                            <Input
                                value={curBillingStreet2}
                                onChange={(e) => setCurBillingStreet2(e.target.value)}
                                style={{ width: '250px' }}
                            />
                        </Space>
                    </Space>

                    {/* Address Row 2 */}
                    <Space direction="horizontal" size={50}>
                        <Space direction="vertical" size="small" style={{ flex: 1 }}>
                            <Text strong>Postal code *</Text>
                            <Input
                                value={curBillingPostcode}
                                onChange={(e) => setCurBillingPostcode(e.target.value)}
                                style={{ width: '250px' }}
                            />
                        </Space>
                        <Space direction="vertical" size="small" style={{ flex: 1 }}>
                            <Text strong>City *</Text>
                            <Input
                                value={curBillingCity}
                                onChange={(e) => setCurBillingCity(e.target.value)}
                                style={{ width: '250px' }}
                            />
                        </Space>
                    </Space>

                    {/* Address Row 3 */}
                    <Space direction="horizontal" size={50}>
                        <Space direction="vertical" size="small" style={{ flex: 1 }}>
                            <Text strong>State / Province</Text>
                            <Input
                                value={curBillingProvince}
                                onChange={(e) => setCurBillingProvince(e.target.value)}
                                style={{ width: '250px' }}
                            />
                        </Space>
                        <Space direction="vertical" size="small" style={{ flex: 1 }}>
                            <Text strong>Country *</Text>
                            <CountryDropdown
                                value={curBillingCountry}
                                onChange={(value) => setCurBillingCountry(value)}
                                status={billingCountryStatus ? "" : "error"}
                            />
                        </Space>
                    </Space>
                </Space>

            {/* Billing Details Section */}
            <Space direction="vertical" size="small">
                <h2 className='header-letter-spacing'>Billing details</h2>
                <p>Fill in the billing details in the fields below.</p>

                <Space direction="horizontal" size={50}>
                    <Space direction="vertical" size="small" style={{ flex: 1 }}>
                        <Text strong>Full name *</Text>
                        <Input
                            value={curBillingFullName}
                            onChange={(e) => setCurBillingFullName(e.target.value)}
                            status={fullNameStatus ? "" : "error"}
                            style={{ width: '250px' }}
                        />
                    </Space>
                    <Space direction="vertical" size="small" style={{ flex: 1 }}>
                        <Text strong>Email *</Text>
                        <Input
                            value={curBillingEmail}
                            onChange={(e) => setCurBillingEmail(e.target.value)}
                            status={emailStatus ? "" : "error"}
                            style={{ width: '250px' }}
                        />
                    </Space>
                </Space>
                <Space direction="horizontal" size={50}>
                    <Space direction="vertical" size="small" style={{ flex: 1 }}>
                        <Text strong>Invoice Reference Number</Text>
                        <Input
                            value={curInvoiceRefNumber}
                            onChange={(e) => setCurInvoiceRefNumber(e.target.value)}
                            style={{ width: '250px' }}
                        />
                    </Space>
                </Space>
            </Space>
        </Space>
    );
};


// Team Members Details
export const TeamMembersDetails = ({ context }) => {
    // Context
    const { teamMembers: contextTeamMembers, companyId } = useContext(context);
    const { inviteUser, deleteUser } = useContext(AuthContext);

    // State - Table
    const [dataSource, setDataSource] = useState([]);
    const [searchText, setSearchText] = useState('');

    // Get current user email 
    const currentUserEmail = localStorageService.getUserData()?.email;

    // State - Invite Form
    const [curFirstName, setCurFirstName] = useState('');
    const [curLastName, setCurLastName] = useState('');
    const [curEmail, setCurEmail] = useState('');
    const [firstNameStatus, setFirstNameStatus] = useState(true);
    const [lastNameStatus, setLastNameStatus] = useState(true);
    const [emailStatus, setEmailStatus] = useState(true);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    // State - UI
    const [secondDrawerVisible, setSecondDrawerVisible] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [loading, setLoading] = useState(false);

    // Constants
    const defaultPlacement = "right";
    const isFormValid = curFirstName?.length > 0 && 
                       curLastName?.length > 0 && 
                       curEmail?.length > 0;

    // Effects
    useEffect(() => {
        if (!contextTeamMembers || contextTeamMembers.length === 0) {
            setDataSource([]); // Clear the table if no team members
            return;
        }
        if (contextTeamMembers && contextTeamMembers.length > 0) {
            // First, find the current user's data
            const currentUser = contextTeamMembers.find(member => member.email === currentUserEmail);
            
            // Filter and sort other members, excluding admin users
            const otherMembers = contextTeamMembers
                .filter(member => member.email !== currentUserEmail)
                .filter(member => !member.is_admin) // Filter out admin users
                .sort((a, b) => new Date(b.date_joined) - new Date(a.date_joined));
            
            // Format other members
            const formattedOtherMembers = otherMembers.map((member) => ({
                key: member.email,
                id: member.email,
                email: member.email,
                date_joined: member.date_joined ? new Date(member.date_joined).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                }) : 'N/A',
                isCurrentUser: false
            }));

            // Create final data array with current user at top
            let formattedData = [];
            if (currentUser) {
                formattedData = [
                    {
                        key: currentUser.email,
                        id: currentUser.email,
                        email: currentUser.email,
                        date_joined: currentUser.date_joined ? new Date(currentUser.date_joined).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        }) : 'N/A',
                        isCurrentUser: true
                    },
                    ...formattedOtherMembers
                ];
            } else {
                formattedData = formattedOtherMembers;
            }

            setDataSource(formattedData);
        }
    }, [contextTeamMembers, currentUserEmail]);

    // Table Configuration
    const columns = [
        {
            title: 'E-mail',
            dataIndex: 'email',
            key: 'email',
            render: (text, record) => (
                <Text>
                    {text}
                </Text>
            ),
            align: 'left',
            width: '60%',
        },
        {
            title: 'Date added',
            dataIndex: 'date_joined',
            key: 'date_joined',
            render: (text) => <Text>{text}</Text>,
            align: 'left',
            width: '30%',
        },
        {
            title: '',
            key: 'actions',
            width: '10%',
            render: (_, record) => {
                return (
                    <Button
                        type="text"
                        style={{ border: 'none', padding: 0 }}
                        icon={
                            <svg width="16" height="22" viewBox="0 0 5 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0.654636 6.12687L0.381753 2.0882H4.40351L4.10312 6.12985C4.08584 6.3624 3.89212 6.54225 3.65894 6.54225H1.09903C0.864692 6.54225 0.670433 6.36067 0.654636 6.12687Z" stroke="#020202" strokeWidth="0.254517"/>
                            <path d="M0.271374 1.99717C0.212126 1.64756 0.437746 1.30964 0.787839 1.2333L3.89625 0.555429C4.24634 0.479082 4.59216 0.692376 4.68386 1.03492L0.271374 1.99717Z" stroke="#020202" strokeWidth="0.190888"/>
                            <path d="M1.63799 1.04839C1.64122 0.860993 1.7724 0.693455 1.96348 0.651785L2.58516 0.516212C2.77624 0.474542 2.96527 0.572251 3.04623 0.741288L1.63799 1.04839Z" stroke="#020202" strokeWidth="0.190888"/>
                            <line x1="1.34905" y1="2.8154" x2="1.55855" y2="5.5462" stroke="#020202" strokeWidth="0.190888" strokeLinecap="round"/>
                            <line x1="0.0954439" y1="-0.0954439" x2="2.83427" y2="-0.0954439" transform="matrix(-0.0764943 0.99707 0.99707 0.0764943 3.55469 2.72461)" stroke="#020202" strokeWidth="0.190888" strokeLinecap="round"/>
                            <line x1="2.40794" y1="2.82005" x2="2.40794" y2="5.55611" stroke="#020202" strokeWidth="0.190888" strokeLinecap="round"/>
                            </svg>
                        }
                        onClick={() => {
                            setUserToDelete(record);
                            setIsDeleteModalVisible(true);
                        }}
                        className="delete-button"
                    />
                );
            },
        }
    ];

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchText(value);
        
        if (!contextTeamMembers) return;

        // First, find current user
        const currentUserData = contextTeamMembers.find(member => member.email === currentUserEmail);
        
        // Filter and format other members
        const otherMembers = contextTeamMembers
            .filter(member => member.email !== currentUserEmail)
            .filter(member => member.email && member.email.toLowerCase().includes(value))
            .sort((a, b) => new Date(b.date_joined) - new Date(a.date_joined))
            .map((member) => ({
                key: member.email,
                id: member.email,
                email: member.email,
                date_joined: member.date_joined ? new Date(member.date_joined).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                }) : 'N/A',
                isCurrentUser: false
            }));

        // Include current user in results if it matches search
        let formattedData = [];
        if (currentUserData && currentUserEmail.toLowerCase().includes(value)) {
            formattedData = [
                {
                    key: currentUserEmail,
                    id: currentUserEmail,
                    email: currentUserEmail,
                    date_joined: currentUserData.date_joined ? new Date(currentUserData.date_joined).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    }) : 'N/A',
                    isCurrentUser: true
                },
                ...otherMembers
            ];
        } else {
            formattedData = otherMembers;
        }

        setDataSource(formattedData);
    };

    const handleInviteSubmit = async () => {
        setHasSubmitted(true);
        try {
            if (!curFirstName?.trim()) {
                setFirstNameStatus(false);
                message.error('First name is required');
                return;
            }
    
            if (!curLastName?.trim()) {
                setLastNameStatus(false);
                message.error('Last name is required');
                return;
            }
    
            if (!companyId) {
                message.error('Company ID is missing');
                return;
            }
    
            // Send invitation
            message.loading({ content: 'Inviting user...', key: 'inviteUser' });
            const response = await inviteUser({
                email: curEmail.trim(),
                first_name: curFirstName.trim(),
                last_name: curLastName.trim(),
                company_id: companyId,
            });
    
            if (response.result) {
                handleInviteSuccess(response);
            } else {
                handleInviteError(response);
            }
        } catch (error) {
            message.error({ 
                content: 'Failed to invite user. Please try again.', 
                key: 'inviteUser',
                duration: 3 
            });
        }
    };

    const handleInviteSuccess = async (response) => {
        message.success({ 
            content: response.message,
            key: 'inviteUser', 
            duration: 2 
        });
        setShowSuccessAlert(true);
        setSuccessMessage(response.message);

        // Reset form
        resetForm();

        // Refresh data
        if (context.refreshTeamMembers) {
            await context.refreshTeamMembers();
        }
    };

    const handleInviteError = (response) => {
        message.error({ 
            content: response.error,
            key: 'inviteUser',
            duration: 3 
        });

        if (response.error?.includes('already exists')) {
            setEmailStatus(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!userToDelete) return;
        
        setLoading(true);
        try {
            const response = await deleteUser({
                email: userToDelete.email,
                company_id: companyId
            });
            
            if (response.result) {
                message.success('User deleted successfully');
                
                // Handle self-deletion
                if (userToDelete.email === currentUserEmail) {
                    // Clear all local storage and auth data
                    localStorageService.clearUserData();
                    // Small delay to ensure message is shown before redirect
                    setTimeout(() => {
                        window.location.href = '/login';
                    }, 1500);
                } else {
                    // Only update the local state if we're not deleting ourselves
                    setDataSource(prevData => prevData.filter(item => item.email !== userToDelete.email));
                }
            } else {
                message.error(response.error || 'Failed to delete user');
            }
        } catch (error) {
            message.error(`Failed to delete user: ${error.message}`);
        } finally {
            setLoading(false);
            setIsDeleteModalVisible(false);
            setUserToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setIsDeleteModalVisible(false);
        setUserToDelete(null);
    };

    // Add confirmation text for self-deletion
    const getDeleteModalContent = () => {
        if (userToDelete?.email === currentUserEmail) {
            return (
                <>
                    <p>Are you sure you want to delete your own account? This action cannot be undone.</p>
                    <p>You will be logged out and redirected to the login page.</p>
                </>
            );
        }
        return <p>Are you sure you want to delete this user?</p>;
    };

    // UI Handlers
    const showSecondDrawer = () => setSecondDrawerVisible(true);
    const closeSecondDrawer = () => {
        setSecondDrawerVisible(false);
        resetForm();
        setShowSuccessAlert(false);
    };
    

    // Helper Functions
    const resetForm = () => {
        setCurEmail('');
        setCurFirstName('');
        setCurLastName('');
        setEmailStatus(true);
        setFirstNameStatus(true);
        setLastNameStatus(true);
        setHasSubmitted(false);
    };

    // Add state for success message
    const [successMessage, setSuccessMessage] = useState('');

    // Render
    return (
        <div className="team-members-details">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Header */}
                <Space direction="vertical" size={25}>
                    <Space direction="vertical" size={16}>
                        <h2 className='header-letter-spacing'>User management</h2>
                        <p>Manage and invite portal users.</p>
                    </Space>

                    {/* Search and Invite Button */}
                    <div className="search-container">
                        <Input
                            placeholder="search"
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={handleSearch}
                            className="team-members-search"
                        />
                        <Button
                            type="primary"
                            onClick={showSecondDrawer}
                            className="team-members-invite-button"
                        >
                            + invite user
                        </Button>
                    </div>
                </Space>

                {/* Users Table */}
                <Table 
                    dataSource={dataSource}
                    columns={columns}
                    pagination={false}
                    rowClassName="highlight-bottom-border custom-row-height"
                    className="team-members-table"
                    locale={{ emptyText: 'No team members found' }}
                />
            </Space>

            {/* Invite User Drawer */}
            <Drawer
                placement={defaultPlacement}
                onClose={closeSecondDrawer}
                open={secondDrawerVisible}
                closable={true}
                maskClosable={true}
                rootClassName="invite-drawer"
                className="custom-drawer-header"
            >
                <Space direction="vertical">
                    {/* Drawer Header */}
                    <Space direction="vertical" >
                        <Space direction="vertical" >
                            <h2 className='header-letter-spacing'>Invite user</h2>
                            <p>Fill in the fields below to invite a new user to the Code Cube portal.</p>
                        </Space>

                        {/* Success Alert */}
                            {showSuccessAlert && (
                        <Space direction="vertical" size={50}>
                                <div className="custom-alert">
                                    <span className="alert-icon">📨</span>
                                    <div className="alert-content">
                                        <div className="alert-message">Invitation sent!</div>
                                        <div className="alert-description">
                                            {successMessage === "User successfully added to company" 
                                                ? "The user has been added to your company. They will receive an email with instructions to access the portal."
                                                : "You have successfully invited the user. They will receive an email with further instructions to create an account."
                                            }
                                        </div>
                                    </div>
                                </div>
                        </Space>
                            )}

                        {/* Invite Form */}
                        <Space direction="vertical" size="middle">
                            <Space direction="horizontal" size={50}>
                                <Space direction="vertical" size="small" style={{ flex: 1 }}>
                                    <Text strong>First name *</Text>
                                    <Input
                                        value={curFirstName}
                                        onChange={(e) => setCurFirstName(e.target.value)}
                                        status={firstNameStatus ? "" : "error"}
                                        style={{ width: '250px' }}
                                    />
                                </Space>
                                <Space direction="vertical" size="small" style={{ flex: 1 }}>
                                    <Text strong>Last name *</Text>
                                    <Input
                                        value={curLastName}
                                        onChange={(e) => setCurLastName(e.target.value)}
                                        status={lastNameStatus ? "" : "error"}
                                        style={{ width: '250px' }}
                                    />
                                </Space>
                            </Space>

                            <Space direction="vertical" size="small">
                                <Text strong>Email *</Text>
                                <Text>Add your personal email address, this email will also be used as your username in the portal.</Text>
                                <Space.Compact size="middle">
                                    <Input
                                        placeholder="hello@code-cube.io"
                                        value={curEmail}
                                        type="email"
                                        onChange={(e) => setCurEmail(e.target.value)}
                                        style={{ width: '250px' }}
                                    />
                                </Space.Compact>
                            </Space>
                        </Space>
                    </Space>

                    {/* Submit Button */}
                    <Space direction="vertical" size="large" style={{ width: '100%', marginTop: '20px' }}>
                        <Popover
                            content={!isFormValid ? "Please fill in all required fields to proceed" : null}
                            trigger="hover"
                        >
                            <Button
                                type="primary"
                                onClick={handleInviteSubmit}
                                disabled={!isFormValid}
                                className={`team-members-invite-submit ${isFormValid ? 'enabled' : 'disabled'}`}
                            >
                                invite
                            </Button>
                        </Popover>
                    </Space>
                </Space>
            </Drawer>

            {/* Delete Confirmation Modal */}
            <Modal
                centered
                title="Delete user"
                open={isDeleteModalVisible}
                onOk={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ 
                    type: 'primary',
                    danger: userToDelete?.email === currentUserEmail
                }}
                cancelButtonProps={{ type: 'default' }}
            >
                {getDeleteModalContent()}
            </Modal>
        </div>
    );
};


